import { useEffect, useState } from "react";
import { Thermometer } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DeviceTile } from "./DeviceTile";
import { ThermostatDial } from "./ThermostatDial";

interface ClimateTileProps {
  name: string;
  /** Human-readable state string, e.g. "21°" or "Off". */
  state: string;
  active?: boolean;
  available?: boolean;
  /** Current target temperature. When null, the tile is read-only (no dialog). */
  targetTemperature: number | null;
  /** Current measured temperature shown in the dial's "Now X°" label. */
  measuredTemperature?: number | null;
  min?: number;
  max?: number;
  /** Called when the user commits a new target temperature via the dial. */
  onCommitTarget: (value: number) => void;
  className?: string;
}

/**
 * Climate device tile that opens a thermostat dial dialog on tap.
 * When targetTemperature is null the tile renders read-only (no dialog trigger).
 *
 * The Dialog is CONTROLLED and opened via DeviceTile's own `onToggle`. We do not
 * use `<DialogTrigger asChild>` here because DeviceTile has a fixed prop signature
 * and is not a transparent prop-forwarding primitive — it drops the
 * onClick/aria-haspopup/ref that Radix injects into an asChild trigger, so the
 * trigger's click/keyboard handlers would never reach the DOM node. Routing
 * activation through `onToggle` gives us role="button" + tabIndex + Enter/Space
 * handling (all provided by DeviceTile/Tile when onToggle is present) for free.
 */
export function ClimateTile({
  name,
  state,
  active = false,
  available = true,
  targetTemperature,
  measuredTemperature,
  min = 5,
  max = 30,
  onCommitTarget,
  className,
}: ClimateTileProps) {
  const [open, setOpen] = useState(false);
  // Local dial state; syncs from prop when the dialog opens.
  const [localTarget, setLocalTarget] = useState<number>(targetTemperature ?? min);

  // Sync local state from prop each time the dialog opens.
  useEffect(() => {
    if (open && targetTemperature !== null && targetTemperature !== undefined) {
      setLocalTarget(targetTemperature);
    }
  }, [open, targetTemperature]);

  // Interactive (opens the dialog) only when there is a target temperature to
  // control and the device is available. Otherwise the tile is read-only.
  const hasTrigger = targetTemperature !== null && targetTemperature !== undefined && available;

  return (
    <>
      <DeviceTile
        name={name}
        icon={<Thermometer />}
        domain="climate"
        state={state}
        active={active}
        available={available}
        onToggle={hasTrigger ? () => setOpen(true) : undefined}
        className={className}
      />
      {hasTrigger && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="flex flex-col items-center gap-6 sm:max-w-xs">
            <DialogHeader className="w-full">
              <DialogTitle>{name}</DialogTitle>
            </DialogHeader>
            <ThermostatDial
              value={localTarget}
              measured={measuredTemperature ?? undefined}
              min={min}
              max={max}
              onChange={setLocalTarget}
              onCommit={(v) => {
                onCommitTarget(v);
                // Dialog stays open per spec: keep interacting after a commit.
              }}
              disabled={!available}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
